import type { WidgetFeatures } from '../types';

/**
 * Documentos aceitos — espelha a lista do backend
 * (TROIA-V3-BACKEND/src/utils/attachment-types.ts), a mesma do chat do painel.
 *
 * As extensões são obrigatórias: o navegador não conhece o tipo de vários
 * desses arquivos (.pfx, .ofx, .ret, .md...) e manda `File.type` vazio — só
 * com o tipo o seletor nem oferecia o certificado digital (TCK-133).
 */
const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  'application/rtf',
  'text/plain',
  'text/markdown',
  'text/x-markdown',
  'text/csv',
  'application/json',
  'application/xml',
  'text/xml',
  'application/x-ofx',
  'application/x-pkcs12',
  'application/pkcs12',
  'application/x-x509-ca-cert',
  'application/x-pem-file',
  'application/pkcs7-mime',
  'application/zip',
  'application/x-zip-compressed',
  'application/vnd.rar',
  'application/x-rar-compressed',
  'application/x-7z-compressed',
];

const DOCUMENT_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.odt', '.ods', '.odp', '.rtf', '.txt', '.md', '.markdown', '.csv', '.json',
  '.xml', '.ofx', '.qif', '.ret', '.rem',
  '.pfx', '.p12', '.cer', '.crt', '.pem', '.p7b',
  '.zip', '.rar', '.7z',
];

const DOCUMENT_MIME_ACCEPT = [...DOCUMENT_MIME_TYPES, ...DOCUMENT_EXTENSIONS].join(',');

/**
 * Monta o valor do atributo `accept` do <input type="file"> a partir das features ativas.
 * Contrato:
 *   buildAcceptTypes(undefined)                     === ''
 *   buildAcceptTypes({ allowImages: true })         === 'image/*'
 *   buildAcceptTypes({ allowImages: true, allowVideo: true })
 *                                                   === 'image/*,video/*'
 *   buildAcceptTypes({ allowDocuments: true })      === DOCUMENT_MIME_ACCEPT
 */
export function buildAcceptTypes(features?: WidgetFeatures): string {
  const accepts: string[] = [];
  if (features?.allowImages) accepts.push('image/*');
  if (features?.allowVideo) accepts.push('video/*');
  if (features?.allowDocuments) accepts.push(DOCUMENT_MIME_ACCEPT);
  return accepts.join(',');
}

/** True se algum tipo de anexo por arquivo (imagem/vídeo/documento) está liberado. */
export function showAttach(features?: WidgetFeatures): boolean {
  return !!(features?.allowImages || features?.allowVideo || features?.allowDocuments);
}

/** True se gravação de áudio está liberada. */
export function showAudio(features?: WidgetFeatures): boolean {
  return !!features?.allowAudio;
}

export const UPLOAD_FAILED_MESSAGE = 'Não foi possível enviar o arquivo. Tente novamente.';

/**
 * Aviso para o visitante quando o upload é recusado. O backend responde
 * `{ message }` em português para recusa do pedido (4xx); erro do servidor
 * (5xx) ou corpo ilegível vira o aviso genérico — a mensagem interna não vaza.
 */
export function uploadErrorMessage(status: number, body: string): string {
  if (status >= 500) return UPLOAD_FAILED_MESSAGE;
  try {
    const parsed = JSON.parse(body) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message.trim()) return parsed.message;
  } catch {
    // corpo não é JSON
  }
  return UPLOAD_FAILED_MESSAGE;
}
